import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNotificationTemplates, useTenantNotificationRules } from "services/hooks/rentals";
import {
  CHANNEL_OPTIONS,
  TENANT_NOTIFICATION_TYPE_OPTIONS,
  TENANT_SCOPE_OPTIONS,
} from "../constants";

const TenantRulesTab = () => {
  const {
    templates,
    templateActionError,
  } = useNotificationTemplates();
  const {
    rules,
    isLoadingRules,
    errorRules,
    isSubmittingRule,
    ruleActionError,
    createRule,
    updateRule,
    deleteRule,
  } = useTenantNotificationRules();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    scope: "ALL_TENANCIES",
    notification_type: "BEFORE_DUE",
    day_offset: 0,
    channel: "EMAIL",
    related_template: "",
    is_active: true,
  });

  const templateOptions = useMemo(
    () => templates.map((template) => ({ id: template.id, label: `${template.name} (${template.channel})` })),
    [templates]
  );

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      related_organization: "",
      name: "",
      scope: "ALL_TENANCIES",
      notification_type: "BEFORE_DUE",
      day_offset: 0,
      channel: "EMAIL",
      related_template: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      scope: row.scope || "ALL_TENANCIES",
      notification_type: row.notification_type || "BEFORE_DUE",
      day_offset: row.day_offset ?? 0,
      channel: row.channel || "EMAIL",
      related_template: row.related_template?.id ?? row.related_template ?? "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.related_template) {
      setMessage({ type: "error", text: "Rule name and template are required." });
      return;
    }
    const payload = {
      related_organization: form.related_organization ? Number(form.related_organization) : null,
      name: form.name.trim(),
      scope: form.scope,
      notification_type: form.notification_type,
      day_offset: Number(form.day_offset),
      channel: form.channel,
      related_template: Number(form.related_template),
      is_active: form.is_active,
    };
    const response = editingId
      ? await updateRule(editingId, payload)
      : await createRule(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Rule updated successfully." : "Rule created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save rule." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tenant rule?")) return;
    const response = await deleteRule(id);
    if (response.success) {
      setMessage({ type: "success", text: "Rule deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete rule." });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Tenant Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Rule
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorRules && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load tenant rules.
        </Alert>
      )}

      {(ruleActionError || templateActionError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ruleActionError || templateActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingRules ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>No tenant rules found.</TableCell>
                </TableRow>
              ) : (
                rules.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.notification_type || "—"}</TableCell>
                    <TableCell>{row.day_offset ?? 0}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Tenant Rule" : "Create Tenant Rule"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Rule Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Scope</InputLabel>
            <Select label="Scope" value={form.scope} onChange={(event) => setForm((prev) => ({ ...prev, scope: event.target.value }))}>
              {TENANT_SCOPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Notification Type</InputLabel>
            <Select
              label="Notification Type"
              value={form.notification_type}
              onChange={(event) => setForm((prev) => ({ ...prev, notification_type: event.target.value }))}
            >
              {TENANT_NOTIFICATION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            margin="normal"
            label="Day Offset"
            value={form.day_offset}
            onChange={(event) => setForm((prev) => ({ ...prev, day_offset: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={form.channel}
              onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={form.related_template}
              onChange={(event) => setForm((prev) => ({ ...prev, related_template: event.target.value }))}
            >
              {templateOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingRule}>
            {isSubmittingRule ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantRulesTab;
